import React, {useEffect, useState} from "react";
import Card from "../Base/Card";
import {FiEdit3, FiSave, FiX} from "react-icons/fi";
import BaseDatePicker from "../Base/BaseDatePicker";
import {Country, ICountry, IState, State} from "country-state-city";
import SuccessModal from "../SuccessModal";
import {GetUserInfo, UpdateBiodata, UpdateKYC} from "../../redux/selfService/selfServiceSlice";
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Simulate} from "react-dom/test-utils";
import submit = Simulate.submit;
import ErrorViewer from "../ErrorViewer";
import {getLGA, getStates} from "../../redux/utils/utilsSlice";

interface SelectedCSCI {
    country?: ICountry,
    state?: string,
}

const ManageAccount = () => {
    const dispatch = useAppDispatch();
    const [isEditMode, setEditMode] = useState(false);
    const [dateOfBirth, setDateOfBirth] = useState<Date>();
    const [selectedCSC, setSelectedCSC] = useState<SelectedCSCI>()
    const [countryStates, setCountryStates] = useState<IState[]>([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const { userInfo, isLoading } = useAppSelector((state) => state?.selfService);
    const { allStates, lgas } = useAppSelector((state) => state?.utils);


    useEffect(()=>{
        dispatch(GetUserInfo());
        dispatch(getStates());
    }, [])

    useEffect(()=>{
        reset({
            mobileNumber: userInfo?.mobileNumber,
            house_Plot_No: userInfo?.house_Plot_No,
            streetName: userInfo?.streetName,
            city: userInfo?.city,
            stateCode: userInfo?.stateCode,
            localGovernment: userInfo?.localGovernment,
        })
    }, [userInfo])

    const validationSchema = Yup.object().shape({
        mobileNumber: Yup.string().required("Mobile number is required"),
        house_Plot_No: Yup.string().required("House plot number is required"),
        streetName: Yup.string().required("Street name is required"),
        city: Yup.string().required("City is required"),
        stateCode: Yup.string().required("State code is required"),
        localGovernment: Yup.string().required("Local government is required"),
    });

    const { handleSubmit, register, setValue, reset, formState: { errors },} = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            mobileNumber: userInfo?.mobileNumber,
            house_Plot_No: userInfo?.house_Plot_No,
            streetName: userInfo?.streetName,
            city: userInfo?.city,
            stateCode: userInfo?.stateCode,
            localGovernment: userInfo?.localGovernment,
        }
    });

    const onSubmit = async (values: any)=>{
        dispatch(UpdateBiodata(values)).then((res)=>{
            if(res?.type==="selfService/UpdateBiodata/fulfilled" && res.payload.statusCode === "00"){
                setShowSuccessModal(true)
            }
        })
    }
    const onStateCodeChanged = (stateCode: string)=>{
        dispatch(getLGA({ stateCode }))
    }
    const toggleEditMode = () => setEditMode(!isEditMode)

    const toggleShowSuccessModal = ()=> setShowSuccessModal(!showSuccessModal);

    const doneEditing = ()=>{
        toggleShowSuccessModal();
        toggleEditMode();
    }

    return (
        <div className="">
            <SuccessModal
                showModal={showSuccessModal}
                onClose={doneEditing}
                note={"Your account has been updated successfully."}
            />
            <div className="flex justify-end text-primary mb-4">
                {!isEditMode ?
                    <button className="btn btn-text text-xl font-semibold px-4" onClick={toggleEditMode}>
                        <FiEdit3 className="mr-2"/> Edit
                    </button>
                    :
                    <>
                        <button className="btn btn-outline font-semibold mr-4" onClick={toggleEditMode}>
                            <FiX className="mr-1 text-xl"/>
                            Cancel
                        </button>
                        <button className="btn btn-primary font-semibold" onClick={handleSubmit(onSubmit)}>
                            <FiSave className="mr-1 text-xl"/>Save
                        </button>
                    </>
                }
            </div>
            {!isEditMode ?
                <div className="w-full">
                    <Card className="mb-8 bg-grayscaleBG">
                        <div className="section-card">
                            <div className="pt-12">
                                Your Personal Information
                            </div>
                            <table className="w-full">
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                First Name:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.firstname}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Middle Name:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.othername}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Surame:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.surname}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Gender:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.gender}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Email Address:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.email}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Mobile Number:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.mobileNumber}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Apartment Suite Number:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.house_Plot_No}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Street Address:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.streetName}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                City:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.city}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Local Government Area
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.localGovernment}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                State:
                                            </div>
                                            <div className="info__group--text">
                                                {userInfo?.state}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </Card>
                    {/*
                    <Card className="mb-8 bg-grayscaleBG">
                        <div className="section-card">
                            <div className="pt-12">
                                Your Employment Information
                            </div>
                            <table className="w-full">
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Company Name:
                                            </div>
                                            <div className="info__group--text">
                                                Dee & Tee Consulting Company
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Apartment Suite Number:
                                            </div>
                                            <div className="info__group--text">
                                                5B
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Street Address:
                                            </div>
                                            <div className="info__group--text">
                                                Odeku Crescent
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                City:
                                            </div>
                                            <div className="info__group--text">
                                                Victoria Island
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                State:
                                            </div>
                                            <div className="info__group--text">
                                                Lagos
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Email Address:
                                            </div>
                                            <div className="info__group--text">
                                                Nil
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Phone Number:
                                            </div>
                                            <div className="info__group--text">
                                                Nil
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </Card>
                    <Card className="mb-8 bg-grayscaleBG">
                        <div className="section-card">
                            <div className="pt-12">
                                Your Account Information
                            </div>
                            <table className="w-full">
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Account Type:
                                            </div>
                                            <div className="info__group--text">
                                                Nil
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Monthly Contribution Amount:
                                            </div>
                                            <div className="info__group--text">
                                                N45, 000. 00
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                BVN:
                                            </div>
                                            <div className="info__group--text">
                                                006277889010
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                NIN:
                                            </div>
                                            <div className="info__group--text">
                                                Nil
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="info__row">
                                    <td>
                                        <div className="info__group">
                                            <div className="info__group--label">
                                                Referral Code:
                                            </div>
                                            <div className="info__group--text">
                                                Nil
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </Card>
                    */}
                </div>
                :
                <div className="w-full">
                    <Card className="mb-8 bg-grayscaleBG">
                        <div className="section-card">
                            <div className="pt-12">
                                Your Personal Information
                            </div>
                            <div className="w-full">
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="firstName" className="form__label">First Name:</label>
                                        <input placeholder="First Name" value={userInfo?.firstname} type="text"
                                               className="form__input" readOnly />
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="middle-name" className="form__label"> Middle Name:</label>
                                        <input placeholder="Middle Name" value={userInfo?.othername} type="text" id="middle-name"
                                               className="form__input" readOnly />
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="lastname" className="form__label">
                                            Surname:
                                        </label>
                                        <input placeholder="Last Name" value={userInfo?.surname} type="text" id="lastname"
                                               className="form__input" readOnly />
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="gender" className="form__label">Gender:</label>
                                        <select id="gender" defaultValue={userInfo?.gender} className="form__select" disabled>
                                            <option value="M">Male</option>
                                            <option value="F">Female</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="email" className="form__label">
                                            Email Address:
                                        </label>
                                        <input placeholder="Email Address" value={userInfo?.email} type="email" id="email"
                                               className="form__input" readOnly />
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="phone-number" className="form__label">
                                            Mobile Number:
                                        </label>
                                        <input placeholder="Phone Number" type="text" id="phone-number"
                                               className="form__input" {...register("mobileNumber")} />
                                        {errors.mobileNumber && (
                                            <ErrorViewer message={errors.mobileNumber.message as string}/>
                                        )}
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="apartment-suite-number" className="form__label">
                                            Apartment Suite Number:
                                        </label>
                                        <input type="text" id="apartment-suite-number" className="form__input" {...register("house_Plot_No")} />
                                        {errors.house_Plot_No && (
                                            <ErrorViewer message={errors.house_Plot_No.message as string}/>
                                        )}
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="street-address" className="form__label">
                                            Street Address:
                                        </label>
                                        <input placeholder="Street Address" type="text" id="street-address"
                                               {...register("streetName")} className="form__input"/>
                                        {errors.streetName && (
                                            <ErrorViewer message={errors.streetName.message as string}/>
                                        )}
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="city" className="form__label">City:</label>
                                        <input placeholder="City" type="text" id="city" className="form__input" {...register("city")} />
                                        {errors.city && (
                                            <ErrorViewer message={errors.city.message as string}/>
                                        )}
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="state" className="form__label">
                                            State:
                                        </label>
                                        <select id="state" defaultValue={userInfo?.stateCode}
                                                className="form__select"
                                                onChange={(e) => {
                                                    onStateCodeChanged(e.target.value);
                                                    setValue('stateCode', e.target.value);
                                                    setValue('localGovernment', '');
                                                }}
                                        >
                                            <option value="" disabled>Select State</option>
                                            {allStates && Object.keys(allStates).map((a) => (
                                                <option value={a} key={a}>{allStates[a]}</option>
                                            ))}
                                        </select>
                                        {errors.stateCode && (
                                            <ErrorViewer message={errors.stateCode.message as string}/>
                                        )}
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="lga" className="form__label">
                                            LGA
                                        </label>
                                        <select id="state" defaultValue=""
                                                className="form__select"
                                                {...register('localGovernment')}
                                        >
                                            <option value="" disabled>Select LGA</option>
                                            {lgas && Object.keys(lgas).map((lgaKey) => (
                                                <option value={lgaKey} key={lgaKey}>{lgas[lgaKey]}</option>
                                            ))}
                                        </select>
                                        {errors.localGovernment && (
                                            <ErrorViewer message={errors.localGovernment.message as string}/>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                    {/*
                    <Card className="mb-8 bg-grayscaleBG">
                        <div className="section-card">
                            <div className="pt-12">
                                Your Employment Information
                            </div>
                            <div className="w-full">
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="company-name" className="form__label">
                                            Company Name:
                                        </label>
                                        <input placeholder="Company Name" type="text" id="company-name"
                                               className="form__input"/>
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="company-location" className="form__label">
                                            Apartment Suite Number:
                                        </label>
                                        <input type="text" id="company-location" className="form__input"/>
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="company-street" className="form__label">
                                            Street Address:
                                        </label>
                                        <input placeholder="Company street Address" type="text" id="company-street"
                                               className="form__input"/>
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="form" className="form__label">
                                            City:
                                        </label>
                                        <input placeholder="Company city" type="text" id="city"
                                               className="form__input"/>
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="company-state" className="form__label">
                                            State:
                                        </label>
                                        <select id="company-state" className="form__select">
                                            {
                                                nigeriaStates?.map((state, index) => (
                                                    <option key={`${state.isoCode}-${index}`}
                                                            value={state.name}>{state.name}</option>
                                                ))
                                            }
                                        </select>

                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="company-email" className="form__label">
                                            Email Address:
                                        </label>
                                        <input placeholder="Company Email" type="email" className="form__input"
                                               id="company-email"/>
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="company-phone" className="form__label">
                                            Phone Number:
                                        </label>
                                        <input type="text" className="form__input" id="company-phone"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                    <Card className="mb-8 bg-grayscaleBG">
                        <div className="section-card">
                            <div className="pt-12">
                                Your Account Information
                            </div>
                            <div className="w-full">
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="account-type" className="form__label">
                                            Account Type:
                                        </label>
                                        <input type="text" id="account-type" className="form__input"/>
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="monthly-amount" className="form__label">
                                            Monthly Contribution:
                                        </label>
                                        <input type="number" id="monthly-amount" className="form__input"/>
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="bvn" className="form__label">
                                            BVN:
                                        </label>
                                        <input type="text" id="bvn" className="form__input"/>
                                    </div>
                                    <div className="form__group">
                                        <label htmlFor="nin" className="form__label">
                                            NIN:
                                        </label>
                                        <input type="text" id="nin" className="form__input"/>
                                    </div>
                                </div>
                                <div className="form__row">
                                    <div className="form__group">
                                        <label htmlFor="referral-code" className="form__label">
                                            Referral Code:
                                        </label>
                                        <input type="text" id="referral-code" className="form__input"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                    */}
                </div>
            }


        </div>
    );
}
export default ManageAccount;